import { Accordion } from '@coop/components';
import classNames from 'classnames';
import type { PropsWithChildren } from 'react';

import Heading from '../../../../../common/components/Heading';
import { useAppSelector } from '../../../../../common/hooks/useThunkDispatch';
import { featureFlagsSelectors } from '../../../../../common/selectors/featureFlagsSelectors';
import styles from './ProductDetailsAccordion.module.scss';
import type {
    ProductDetailsAccordionItemType,
    ProductDetailsAccordionProps,
} from './ProductDetailsAccordion.types';
import ProductFacts from './ProductFacts';
import ProductInformation from './ProductInformation';
import ProductNutritionTable from './ProductNutritionTable';
import ProductPriceHistory from './ProductPriceHistory';

const ProductDetailsAccordion: React.FC<React.PropsWithChildren<ProductDetailsAccordionProps>> = (
    props,
) => {
    const isPILPricesLogicEnabled = useAppSelector(
        featureFlagsSelectors.selectPILPricesLogicEnabled,
    );

    return (
        <Accordion.Root
            type="single"
            collapsible
            className={classNames(styles.Root, props.className)}
            theme="gray"
            rounded
            defaultValue={props.defaultValue}
        >
            {(props.description || props.isMagazine) && (
                <ProductDetailsAccordionItem value="description" heading="Beskrivning">
                    <ProductInformation
                        description={props.description}
                        isMagazine={props.isMagazine}
                        isPharmaceutical={props.isPharmaceutical}
                    />
                </ProductDetailsAccordionItem>
            )}

            <ProductDetailsAccordionItem value="productFacts" heading="Produktfakta">
                <ProductFacts
                    eanCode={props.eanCode}
                    listOfIngredients={props.listOfIngredients}
                    listOfNonFoodIngredients={props.listOfNonFoodIngredients}
                    maxStorageTemperature={props.maxStorageTemperature}
                    declarationOfOrigin={props.declarationOfOrigin}
                    catchData={props.catchData}
                    fishReportingInformations={props.fishReportingInformations}
                    countriesOfOrigin={props.countriesOfOrigin}
                    compulsoryAdditiveLabel={props.compulsoryAdditiveLabel}
                    consumerInstructions={props.consumerInstructions}
                    replacementCountries={props.replacementCountries}
                    preparationInstructionsList={props.preparationInstructionsList}
                    healthattributeLabels={props.healthattributeLabels}
                    healthSafetyPrecautionaryStatementText={
                        props.healthSafetyPrecautionaryStatementText
                    }
                    healthSafetySignalStatementText={props.healthSafetySignalStatementText}
                    healthSafetySignalWordsCode={props.healthSafetySignalWordsCode}
                    storageState={props.storageState}
                    regulatedArticleDescription={props.regulatedArticleDescription}
                    animalFoodData={props.animalFoodData}
                    contactInformation={props.contactInformation}
                    claimReportingInformations={props.claimReportingInformations}
                />
            </ProductDetailsAccordionItem>
            {!!props.nutrientInformation &&
                props.nutrientInformation?.[0].header?.nutrientBasisQuantity &&
                props.nutrientInformation?.[0].header?.nutrientBasisQuantityUnit &&
                !!props.nutritionFacts &&
                !!props.nutritionFacts.length && (
                    <ProductDetailsAccordionItem
                        value="nutritionalContent"
                        heading="Näringsinnehåll"
                    >
                        <ProductNutritionTable
                            nutritionFacts={props.nutritionFacts}
                            nutrientInformation={props.nutrientInformation}
                        />
                    </ProductDetailsAccordionItem>
                )}

            {/* do not show price history in PDP if PIL prices logic is enabled */}
            {!!props.priceHistory && !isPILPricesLogicEnabled && (
                <ProductDetailsAccordionItem heading="Prishistorik" value="priceHistory">
                    <ProductPriceHistory
                        priceHistory={props.priceHistory}
                        priceType={props.priceType}
                    />
                </ProductDetailsAccordionItem>
            )}
        </Accordion.Root>
    );
};

const ProductDetailsAccordionItem = (
    props: PropsWithChildren<{
        value: ProductDetailsAccordionItemType;
        heading: string;
    }>,
) => {
    const openAllAccordions = window.isPrerenderCrawler || undefined; // The accordions needs to be opened for google to be able to index the content.

    return (
        <Accordion.Item
            value={props.value}
            className={styles.ProductDetailsAccordionItem}
            data-product-information={props.heading}
        >
            <Accordion.Header asChild className={styles.AccordionHeader}>
                <Accordion.Trigger>
                    <Heading element="h2" stylingElement="h5" alignment="start">
                        {props.heading}
                    </Heading>
                </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className={styles.AccordionContent} forceMount={openAllAccordions}>
                {props.children}
            </Accordion.Content>
        </Accordion.Item>
    );
};

export default ProductDetailsAccordion;
